import { useAppContext } from "@/context/AppContext";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useTransactionWithBody } from "./useTransactionWithBody";
import { DefaultError, useMutation } from "@tanstack/react-query";
import { signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { blake2b } from "@noble/hashes/blake2";
import { Address } from "@blaze-cardano/core";

const toSeconds = (value: number) => Math.floor(value / 1000);

export const useHandleTransactionRequest = <T>(transactionType: string) => {
  const {
    state: { blaze, provider },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const headInfo = useHydrozoaHeadInfo();
  const { mutateAsync: transact } = useTransactionWithBody();

  return useMutation<unknown, DefaultError, T>({
    mutationKey: [
      "transaction",
      transactionType,
      blaze,
      provider,
      mainAddress,
      headInfo,
    ],
    mutationFn: async (l2Payload) => {
      if (!headInfo) {
        throw new Error(
          "Sugar Rush is offline right now. Please try again later.",
        );
      }
      const { headId } = headInfo;
      const validityStart = toSeconds(Date.now() - 1_000); // 30s for hydrozoa to validate the transaction
      const validityEnd = validityStart + 31;

      const transaction = {
        l2Payload: Buffer.from(
          JSON.stringify({
            tag: "UserTransaction",
            accountId:
              Address.fromBech32(mainAddress).getProps().paymentPart?.hash,
            actions: [
              {
                tag: transactionType,
                ...l2Payload,
              }
            ]
          }),
        ).toString("hex"),
      };

      const bodyHash = Buffer.from(blake2b(Buffer.from(transaction.l2Payload, "hex"), {
        dkLen: 32,
      })).toString("hex");

      const header = {
        headId,
        validityStart,
        validityEnd,
        bodyHash,
      };

      const { key: coseKey, signature: coseSignature } =
        await signWithEphemeralWallet(
          provider,
          mainAddress,
          Buffer.from(JSON.stringify(header)),
        );

      const body = {
        header,
        transaction,
        coseKey,
        coseSignature,
      };

      const result = await transact(body);

      result;
    },
  });
};
